import { useMemo, useState } from 'react';
import type { AreaTransferView } from '@pqrs/contracts';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { SelectorBuscable } from '@xiltrion/ui/bloques/selectores';
import { Spinner } from '@xiltrion/ui/spinner';
import { Textarea } from '@xiltrion/ui/textarea';
import { Field } from '@/components/ui';
import { errorMessage, notify } from '@/features/cases/lib';
import { useSupportUnits } from '@/features/support/api';
import { useCancelAreaTransfer, useCaseAreaTransfers, useCreateAreaTransfer, useDecideAreaTransfer } from './api';

/**
 * Trasladar el caso a otra área. `direct` (líder del área, Administrador): se traslada de una vez. `request` (quien
 * responde el caso): queda pendiente hasta que lo apruebe quien aprueba en el área.
 */
export function AreaTransferDialog({ caseId, mode, ownUnitId, onHide }: { caseId: string; mode: 'direct' | 'request'; ownUnitId: string | null; onHide: () => void }) {
  const create = useCreateAreaTransfer(caseId);
  const units = useSupportUnits();
  const options = useMemo(() => (units.data ?? []).filter((u) => u.id !== ownUnitId).map((u) => ({
    value: u.id, label: u.name, deshabilitada: !u.attended,
    descripcion: !u.attended ? 'Sin quién responda: no tiene líder ni personas con «Responde»' : undefined,
  })), [units.data, ownUnitId]);
  const [toUnitId, setToUnitId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const valid = !!toUnitId && reason.trim().length >= 10;
  const submit = () => create.mutate({ toUnitId: toUnitId!, reason: reason.trim() }, {
    onSuccess: (t) => {
      if (t.status === 'APPROVED') notify('success', `Caso trasladado a ${t.toUnit.name}`, 'El reparto automático eligió a su responsable en el área.');
      else notify('success', 'Traslado pedido', `${t.approver?.name ?? 'Quien aprueba en el área'} recibe un aviso para aprobarlo.`);
      onHide();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !create.isPending) onHide(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{mode === 'direct' ? 'Trasladar a otra área' : 'Pedir traslado a otra área'}</DialogTitle>
          <DialogDescription>{mode === 'direct'
            ? 'El caso pasa al área elegida y el reparto automático le asigna un responsable allí, con las tareas que tenía el anterior.'
            : 'Quien aprueba en tu área recibe la solicitud. Mientras la decide, sigues siendo el responsable del caso.'}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <Field label="Área destino" htmlFor="transfer-unit" required>
            <SelectorBuscable id="transfer-unit" value={toUnitId} onChange={setToUnitId} options={options} cargando={units.isLoading} placeholder="Elegir área…" textoVacio="No hay otras áreas activas" className="w-full" />
          </Field>
          <Field label="Motivo" htmlFor="transfer-reason" required hint="Por qué el caso le corresponde a esa área. Queda en la historia del caso.">
            <Textarea id="transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={2000} placeholder="Por ejemplo: la petición es sobre cupos escolares, que atiende la Secretaría de Educación." />
          </Field>
          {create.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(create.error)}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onHide} disabled={create.isPending}>Cancelar</Button>
          <Button disabled={!valid || create.isPending} onClick={submit}>{create.isPending ? <Spinner /> : <Icono nombre="mdi:swap-horizontal" />} {mode === 'direct' ? 'Trasladar' : 'Pedir traslado'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** El traslado pendiente del caso, arriba del expediente: quien lo decide lo aprueba o rechaza ahí; quien lo pidió puede cancelarlo. */
export function AreaTransferNotice({ caseId }: { caseId: string }) {
  const q = useCaseAreaTransfers(caseId);
  const decide = useDecideAreaTransfer(caseId);
  const [dialog, setDialog] = useState<'reject' | 'cancel' | null>(null);
  const t = q.data?.pending;
  if (!t) return null;
  const approve = () => decide.mutate({ id: t.id, approve: true }, { onSuccess: (r) => notify('success', `Caso trasladado a ${r.toUnit.name}`, 'El reparto automático eligió a su responsable en el área.') });
  return (
    <>
      <Alert variant="warning">
        <Icono nombre="mdi:swap-horizontal" aria-hidden />
        <AlertTitle>Traslado a {t.toUnit.name} pendiente de aprobación</AlertTitle>
        <AlertDescription>
          <p>{t.requestedBy.name} lo pidió{t.fromUnit ? ` desde ${t.fromUnit.name}` : ''}: «{t.reason}»</p>
          <p>{t.approver ? `Lo aprueba ${t.approver.name}` : 'Lo aprueba quien aprueba en el área'} (o el líder del área).</p>
          {decide.isError && <p className="text-destructive">{errorMessage(decide.error)}</p>}
        </AlertDescription>
        {(t.can.decide || t.can.cancel) && (
          <AlertAction className="flex gap-2">
            {t.can.decide && <Button size="sm" variant="success" disabled={decide.isPending} onClick={approve}>{decide.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Aprobar</Button>}
            {t.can.decide && <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => setDialog('reject')}><Icono nombre="mdi:close" /> Rechazar</Button>}
            {t.can.cancel && !t.can.decide && <Button size="sm" variant="outline" onClick={() => setDialog('cancel')}><Icono nombre="mdi:close" /> Cancelar solicitud</Button>}
          </AlertAction>
        )}
      </Alert>
      {dialog && <ReasonDialog caseId={caseId} t={t} kind={dialog} onHide={() => setDialog(null)} />}
    </>
  );
}

/** Rechazar (el motivo llega a quien lo pidió) o cancelar (llega a quien debía aprobarlo). */
function ReasonDialog({ caseId, t, kind, onHide }: { caseId: string; t: AreaTransferView; kind: 'reject' | 'cancel'; onHide: () => void }) {
  const decide = useDecideAreaTransfer(caseId);
  const cancel = useCancelAreaTransfer(caseId);
  const m = kind === 'reject' ? decide : cancel;
  const [text, setText] = useState('');
  const submit = () => {
    const done = { onSuccess: () => { notify('success', kind === 'reject' ? 'Traslado rechazado' : 'Solicitud de traslado cancelada'); onHide(); } };
    if (kind === 'reject') decide.mutate({ id: t.id, approve: false, comment: text.trim() }, done);
    else cancel.mutate({ id: t.id, reason: text.trim() }, done);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !m.isPending) onHide(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{kind === 'reject' ? `Rechazar el traslado a ${t.toUnit.name}` : 'Cancelar la solicitud de traslado'}</DialogTitle>
          <DialogDescription>{kind === 'reject' ? `${t.requestedBy.name} recibe un aviso con el motivo y el caso sigue en su área.` : 'Quien debía aprobarlo deja de verlo como pendiente. Queda en la historia del caso.'}</DialogDescription>
        </DialogHeader>
        <Field label="Motivo" htmlFor="transfer-decision" required><Textarea id="transfer-decision" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={kind === 'reject' ? 2000 : 500} autoFocus /></Field>
        {m.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(m.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={onHide} disabled={m.isPending}>Volver</Button>
          <Button variant="destructive" disabled={text.trim().length < 3 || m.isPending} onClick={submit}>{m.isPending ? <Spinner /> : <Icono nombre="mdi:close" />} {kind === 'reject' ? 'Rechazar traslado' : 'Cancelar solicitud'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
